import { useCallback, useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from "react";

/** A card being dragged: whose it is, where the pointer is, and the card it would be dropped on. */
export interface CardDrag {
  id: string;
  role: string;
  /** the pointer, in viewport px */
  x: number;
  y: number;
  /** a finger or a pen, not a mouse */
  touch: boolean;
  /** the card of the same role under the pointer, or null */
  over: string | null;
}

/** A mouse drags once it has moved this far with the button down (px). */
const MOUSE_SLOP = 6;
/** A finger or a pen holds still this long on a card before it drags (ms); a quicker swipe scrolls the row. */
const HOLD_MS = 350;
/** How far a holding finger may move before the hold counts as a scroll (px). */
const TOUCH_SLOP = 10;
/** Within this distance of a row's sides, or of the window's top or bottom, a drag scrolls it (px). */
const EDGE = 56;

/** Scroll speed at `d` px from an edge, in px a frame: faster nearer the edge. */
const speed = (d: number) => Math.ceil(14 * (1 - Math.max(0, d) / EDGE));

/**
 * Drag a player card onto another card of the same role to merge the two.
 * Cards carry `data-player` and `data-role` and spread `bind` on their outer
 * element; `onDrop(from, into)` gets the two player ids. A mouse drags once it
 * moves; a finger holds still on a card first, so a swipe still scrolls the
 * row. A drop anywhere else, Esc, or a lost pointer puts the card back. While
 * a card is up a finger does not scroll the page, and the row or the page
 * scrolls when the pointer is near its edge, so a card out of view can be
 * reached. The click that ends a drag does not open the card.
 */
export function useCardDrag(onDrop: (from: string, into: string) => void, enabled: boolean) {
  const [drag, setDrag] = useState<CardDrag | null>(null);
  const live = useRef<CardDrag | null>(null);
  const stop = useRef<(() => void) | null>(null);
  const touchDown = useRef(false);
  // until this time (performance.now() ms) a click on a card is the end of a drag
  const swallowUntil = useRef(0);
  const drop = useRef(onDrop);
  drop.current = onDrop;
  useEffect(() => () => stop.current?.(), []);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      swallowUntil.current = 0;
      if (!enabled || e.button !== 0 || !e.isPrimary || stop.current) return;
      if ((e.target as HTMLElement).closest("input, button.nt, .combo, .sheet")) return; // typing a name or assigning a handle
      const card = e.currentTarget;
      const id = card.dataset.player;
      const role = card.dataset.role;
      if (!id || !role) return;
      const row = card.closest<HTMLElement>(".prow");
      const pointer = e.pointerId;
      const touch = e.pointerType !== "mouse";
      const x0 = e.clientX;
      const y0 = e.clientY;
      let x = x0;
      let y = y0;
      let hold = 0;
      let frame = 0;

      const set = (d: CardDrag | null) => {
        live.current = d;
        setDrag(d);
      };
      const under = () => {
        const el = document.elementFromPoint(x, y)?.closest<HTMLElement>("[data-player]");
        return el && el.dataset.role === role && el.dataset.player !== id ? el.dataset.player! : null;
      };
      const tick = () => {
        const d = live.current;
        if (!d) return;
        if (row && row.scrollWidth > row.clientWidth) {
          const r = row.getBoundingClientRect();
          if (x - r.left < EDGE) row.scrollLeft -= speed(x - r.left);
          else if (r.right - x < EDGE) row.scrollLeft += speed(r.right - x);
        }
        if (y < EDGE) window.scrollBy(0, -speed(y));
        else if (window.innerHeight - y < EDGE) window.scrollBy(0, speed(window.innerHeight - y));
        // the cards moved under a still pointer
        const over = under();
        if (over !== d.over) set({ ...d, over });
        frame = requestAnimationFrame(tick);
      };
      const lift = () => {
        set({ id, role, x, y, touch, over: under() });
        document.documentElement.classList.add("dragging");
        if (touch) navigator.vibrate?.(10);
        frame = requestAnimationFrame(tick);
      };
      const end = (dropped: boolean) => {
        const d = live.current;
        stop.current?.();
        if (!d) return;
        set(null);
        swallowUntil.current = performance.now() + 500;
        if (dropped && d.over) drop.current(d.id, d.over);
      };
      const move = (ev: PointerEvent) => {
        if (ev.pointerId !== pointer) return;
        x = ev.clientX;
        y = ev.clientY;
        const d = live.current;
        if (d) set({ ...d, x, y, over: under() });
        else if (Math.hypot(x - x0, y - y0) > (touch ? TOUCH_SLOP : MOUSE_SLOP)) {
          if (touch) end(false); // it moved before the hold: a scroll
          else lift();
        }
      };
      const up = (ev: PointerEvent) => ev.pointerId === pointer && end(true);
      const cancel = (ev: PointerEvent) => ev.pointerId === pointer && end(false);
      const key = (ev: KeyboardEvent) => ev.key === "Escape" && end(false);
      const blur = () => end(false);
      // once the card is up, a moving finger moves the card, not the row or the page
      const still = (ev: TouchEvent) => {
        if (live.current && ev.cancelable) ev.preventDefault();
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
      window.addEventListener("pointercancel", cancel);
      window.addEventListener("keydown", key);
      window.addEventListener("blur", blur);
      card.addEventListener("touchmove", still, { passive: false });
      touchDown.current = touch;
      if (touch) hold = window.setTimeout(lift, HOLD_MS);
      stop.current = () => {
        stop.current = null;
        touchDown.current = false;
        clearTimeout(hold);
        cancelAnimationFrame(frame);
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        window.removeEventListener("pointercancel", cancel);
        window.removeEventListener("keydown", key);
        window.removeEventListener("blur", blur);
        card.removeEventListener("touchmove", still);
        document.documentElement.classList.remove("dragging");
      };
    },
    [enabled],
  );

  const bind = {
    onPointerDown,
    onClickCapture: (e: ReactMouseEvent) => {
      if (performance.now() < swallowUntil.current) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    // a held finger would open the browser's menu for the picture
    onContextMenu: (e: ReactMouseEvent) => {
      if (touchDown.current || live.current) e.preventDefault();
    },
    onDragStart: (e: ReactMouseEvent) => e.preventDefault(),
  };
  return { drag, bind };
}
